import { type NavSection } from '@/config/navigation';
import { usePermissions } from '@/lib/hooks/use-permissions';
import { useTenantFeatures } from '@/lib/hooks/use-tenant-features';
import { useOpsMode } from '@/lib/hooks/use-ops-mode';
import { useAccessibleLocations } from '@/lib/hooks/use-accessible-locations';
import {
  useCutObligations,
  type CutObligation,
  type CutObligationState,
} from '@/lib/hooks/use-cut-obligations';
import { type PermissionString } from '@/lib/constants/permissions';

interface NavVisibilityContext {
  hasPermission: (permission: PermissionString) => boolean;
  hasFeature: (feature: string) => boolean;
  isOpsEnabled: boolean;
  totalStockLocations: number;
  locationsLoading: boolean;
  obligations: CutObligationState;
}

export function filterNavSections(
  sections: NavSection[] | undefined,
  visibility: NavVisibilityContext,
): NavSection[] | undefined {
  if (!sections) return undefined;

  return sections
    .map(section => ({
      ...section,
      items: section.items.filter(item => {
        if (item.requiredPermission && !visibility.hasPermission(item.requiredPermission)) return false;
        if (item.featureRequired && !visibility.hasFeature(item.featureRequired)) return false;
        if (item.opsRequired && !visibility.isOpsEnabled) return false;
        if (item.obligationRequired && !visibility.obligations[item.obligationRequired]) return false;
        if (item.minStockLocations !== undefined) {
          if (visibility.locationsLoading) return false;
          if (visibility.totalStockLocations < item.minStockLocations) return false;
        }
        return true;
      }),
    }))
    .filter(section => section.items.length > 0);
}

/** One visibility model shared by desktop Sidebar and mobile LayoutContext. */
export function useFilteredNavSections(sections: NavSection[] | undefined): NavSection[] | undefined {
  const { hasPermission } = usePermissions();
  const { hasFeature } = useTenantFeatures();
  const { isOpsEnabled } = useOpsMode();
  const { totalItems: totalStockLocations, loading: locationsLoading } = useAccessibleLocations();
  const requestedObligations = new Set(
    (sections ?? []).flatMap(section => section.items)
      .map(item => item.obligationRequired)
      .filter((key): key is CutObligation => Boolean(key)),
  );
  const obligations = useCutObligations(requestedObligations);

  return filterNavSections(sections, {
    hasPermission,
    hasFeature,
    isOpsEnabled,
    totalStockLocations,
    locationsLoading,
    obligations,
  });
}
